/**
 * Material node utilities for contenteditable editor
 * These functions create DOM elements for material nodes
 */

// ==================== 颜色工具函数 ====================

/**
 * 检测字符串是否是有效的颜色值
 * 支持: #RGB, #RRGGBB, #RRGGBBAA, rgb(), rgba(), hsl(), hsla(), 颜色名称
 * @param {string} str - 要检测的字符串
 * @returns {boolean}
 */
export const isValidColor = (str) => {
  if (!str || typeof str !== 'string') return false;
  const trimmed = str.trim();

  // HEX 颜色 (#RGB, #RRGGBB, #RRGGBBAA)
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
    return true;
  }

  // rgb/rgba
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(trimmed)) {
    return true;
  }

  // hsl/hsla
  if (/^hsla?\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i.test(trimmed)) {
    return true;
  }

  // CSS 颜色名称（常用）
  const namedColors = [
    'black',
    'white',
    'red',
    'green',
    'blue',
    'yellow',
    'orange',
    'purple',
    'pink',
    'gray',
    'grey',
    'brown',
    'cyan',
    'magenta',
    'lime',
    'maroon',
    'navy',
    'olive',
    'teal',
    'aqua',
    'fuchsia',
    'silver',
    'gold',
    'coral',
    'salmon',
    'tomato',
    'crimson',
    'indigo',
    'violet',
    'plum',
    'orchid',
    'lavender',
    'beige',
    'ivory',
    'khaki',
    'tan',
    'chocolate',
    'sienna',
    'peru',
    'wheat',
    'moccasin',
    'peachpuff',
    'bisque',
    'blanchedalmond',
    'papayawhip',
    'lemonchiffon',
    'lightgoldenrodyellow',
    'lightyellow',
    'cornsilk',
    'floralwhite',
    'seashell',
    'snow',
    'honeydew',
    'mintcream',
    'azure',
    'aliceblue',
    'ghostwhite',
    'whitesmoke',
    'gainsboro',
    'lightgray',
    'lightgrey',
    'darkgray',
    'darkgrey',
    'dimgray',
    'dimgrey',
    'slategray',
    'slategrey',
    'lightslategray',
    'lightslategrey',
    'transparent',
  ];
  if (namedColors.includes(trimmed.toLowerCase())) {
    return true;
  }

  return false;
};

/**
 * 规范化颜色值为 HEX 格式（用于显示）
 * @param {string} color - 颜色值
 * @returns {string} 规范化的 HEX 颜色或原值
 */
export const normalizeColorToHex = (color) => {
  if (!color) return color;

  // 如果已经是 HEX 格式，直接返回（可能需要补全）
  if (color.startsWith('#')) {
    if (color.length === 4) {
      // #RGB -> #RRGGBB
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    return color.toUpperCase();
  }

  // 使用 canvas 来转换颜色
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = color;
    const computedColor = ctx.fillStyle;
    // fillStyle 会返回 #rrggbb 格式
    if (computedColor.startsWith('#')) {
      return computedColor.toUpperCase();
    }
    // 如果返回 rgb() 格式，转换为 HEX
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
      const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
      const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`.toUpperCase();
    }
  } catch {
    // 转换失败，返回原值
  }
  return color;
};

/**
 * 获取颜色的亮度（用于判断是否使用深色或浅色文字）
 * @param {string} color - HEX 颜色值
 * @returns {number} 0-255 的亮度值
 */
export const getColorLuminance = (color) => {
  if (!color) return 128;

  let hex = color;
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // 使用感知亮度公式
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * 通过 ID 移除颜色 Tag 节点
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} colorId - 颜色 ID
 * @param {Function} onSync - 同步内容回调
 */
export const removeColorTagNodeById = (editorRef, colorId, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-color-id="${colorId}"]`);
  if (node) {
    node.remove();
    onSync?.();
  }
};

/**
 * 通过 ID 移除 Skill Tag 节点
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} skillId - Skill ID
 * @param {Function} onSync - 同步内容回调
 */
export const removeUserSkillTagById = (editorRef, skillId, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-skill-id="${skillId}"]`);
  if (node) {
    node.remove();
    onSync?.();
  }
};

// 素材用途选项（用于图片素材的用途标签）
export const materialUsageKeys = [
  { id: 'background', label: 'Background' },
  { id: 'product', label: 'Product' },
  { id: 'model', label: 'Model' },
  { id: 'logo', label: 'Logo' },
  { id: 'qrcode', label: 'QR Code' },
  { id: 'icon', label: 'Icon' },
  { id: 'decoration', label: 'Decoration' },
  { id: 'texture', label: 'Texture' },
  { id: 'chart', label: 'Chart' },
];

/**
 * 获取素材用途列表
 * @returns {Array} 带有 label 的用途列表
 */
export const getMaterialUsages = () => {
  return materialUsageKeys;
};

// 编辑器标签选项（用于 # 触发的标签菜单）
export const editorTagKeys = [
  { id: 'product', label: 'Product', color: 'blue' },
  { id: 'brand', label: 'Brand', color: 'purple' },
  { id: 'slogan', label: 'Slogan', color: 'green' },
  { id: 'title', label: 'Title', color: 'orange' },
  { id: 'description', label: 'Description', color: 'pink' },
  { id: 'cta', label: 'Call to Action', color: 'red' },
  { id: 'logo', label: 'Logo', color: 'indigo' },
  { id: 'background', label: 'Background', color: 'teal' },
];

/**
 * 获取编辑器标签列表
 * @returns {Array} 带有 label 的标签列表
 */
export const getEditorTags = () => {
  return editorTagKeys;
};

/**
 * 根据查询过滤编辑器标签
 * @param {string} query - 搜索查询
 * @returns {Array} 过滤后的标签列表
 */
export const filterEditorTags = (query) => {
  const tags = getEditorTags();
  if (!query) return tags;
  const lowerQuery = query.toLowerCase();
  return tags.filter(
    (tag) =>
      tag.label.toLowerCase().includes(lowerQuery) || tag.id.toLowerCase().includes(lowerQuery)
  );
};

/**
 * 更新用途标签显示
 * @param {HTMLElement} container - 素材容器元素
 * @param {string} tag - 用途标签
 */
export const updateUsageLabel = (container, tag) => {
  const label = container.querySelector('.usage-trigger');
  if (label) {
    // 更新文本
    const labelText = label.querySelector('span');
    if (labelText) {
      labelText.textContent = tag || 'Add usage';
    }
    // 更新样式
    label.className = `usage-trigger absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full transition-opacity duration-200 cursor-pointer z-10 max-w-[90%] ${
      tag
        ? 'bg-black/60 text-white shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
        : 'bg-black/60 text-white/90 shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
    }`;
  }
};

/**
 * 更新类型指示器图标
 * @param {HTMLElement} container - 素材容器元素
 * @param {boolean} isMaterial - 是否是素材
 */
export const updateTypeIndicator = (container, isMaterial) => {
  const indicator = container.querySelector('.type-indicator');
  if (indicator) {
    indicator.innerHTML = isMaterial ? MATERIAL_ICON : REFERENCE_ICON;
  }
};

/**
 * 创建用途选择弹窗
 * @param {HTMLElement} container - 素材容器元素
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onTagChange - 标签变更回调
 * @param {Function} options.onSync - 同步内容回调
 * @returns {HTMLElement} 弹窗元素
 */
export const createUsagePopover = (container, asset, options = {}) => {
  const { onTagChange, onSync } = options;

  const popover = document.createElement('div');
  popover.className =
    'usage-popover absolute left-0 bottom-full mb-2 w-64 p-3 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-black/5 z-50 hidden';

  // 标题
  const header = document.createElement('div');
  header.className = 'text-xs font-medium text-black/70 mb-2';
  header.textContent = 'Select Usage';
  popover.appendChild(header);

  // 用途网格
  const grid = document.createElement('div');
  grid.className = 'grid grid-cols-3 gap-1.5 mb-2';

  const usages = getMaterialUsages();
  usages.forEach((usage) => {
    const btn = document.createElement('button');
    const isSelected = asset.tag === usage.label;
    btn.className = `px-2 py-1.5 text-xs rounded-lg transition-all ${
      isSelected
        ? 'bg-gradient-to-br from-[#20f6da]/20 to-[#86EEBA]/20 text-black/80 border border-[#20f6da]/30'
        : 'bg-black/5 text-black/60 hover:bg-black/10 border border-transparent'
    }`;
    btn.textContent = usage.label;
    btn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      asset.tag = usage.label;
      container.setAttribute('data-tag', usage.label);
      updateUsageLabel(container, usage.label);
      onTagChange?.(asset, usage.label);
      popover.classList.add('hidden');
      onSync?.();
    };
    grid.appendChild(btn);
  });
  popover.appendChild(grid);

  // 自定义输入
  const customRow = document.createElement('div');
  customRow.className = 'flex gap-1.5';
  const customInput = document.createElement('input');
  customInput.type = 'text';
  customInput.placeholder = 'Custom usage';
  customInput.className =
    'flex-1 text-xs px-2 py-1.5 border border-black/10 rounded-lg bg-white/60 focus:outline-none focus:border-[#20f6da]/40';
  customInput.onclick = (e) => e.stopPropagation();

  const customBtn = document.createElement('button');
  customBtn.className =
    'px-2 py-1.5 text-xs bg-gradient-to-r from-[#20f6da] to-[#86EEBA] text-white rounded-lg hover:shadow-md transition-all';
  customBtn.textContent = 'Add';
  customBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = customInput.value.trim();
    if (value) {
      asset.tag = value;
      container.setAttribute('data-tag', value);
      updateUsageLabel(container, value);
      onTagChange?.(asset, value);
      popover.classList.add('hidden');
      onSync?.();
    }
  };
  customRow.appendChild(customInput);
  customRow.appendChild(customBtn);
  popover.appendChild(customRow);

  return popover;
};

// 默认预览尺寸限制
const DEFAULT_PREVIEW_SIZE = 120;
const MAX_PREVIEW_SIZE = 220;
const MIN_PREVIEW_WIDTH = 120;
const MIN_PREVIEW_HEIGHT = 72;
const MAX_RESIZE_SIZE = 400;

const MATERIAL_ICON = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
const REFERENCE_ICON = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path></svg>`;

const deselectAllMaterialNodes = (exceptContainer = null) => {
  document.querySelectorAll('[data-type="image"]').forEach((node) => {
    if (exceptContainer && node === exceptContainer) return;

    const setSelected = node._setSelected;
    if (typeof setSelected === 'function') {
      setSelected(false);
      return;
    }

    const resizeHandles = node.querySelector('.resize-handles');
    if (resizeHandles) resizeHandles.style.opacity = '0';
    const wrapper = node.querySelector('.img-wrapper');
    if (wrapper) {
      wrapper.classList.remove('border-neutral-900');
      wrapper.classList.add('border-transparent');
    }
  });
};

/**
 * 创建 4 角 resize handles
 * @param {HTMLElement} imgWrapper - 图片包装器
 * @param {HTMLElement} container - 容器元素
 * @param {Object} asset - 素材对象
 * @param {Function} onSync - 同步回调
 * @returns {HTMLElement} handles 容器
 */
const createResizeHandles = (imgWrapper, container, asset, onSync) => {
  const handlesContainer = document.createElement('div');
  handlesContainer.className = 'resize-handles';
  handlesContainer.style.cssText = `
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
    `;

  // 4 个角的配置 - 更紧凑的设计
  const corners = [
    { position: 'nw', cursor: 'nwse-resize', style: 'top: -3px; left: -3px;' },
    { position: 'ne', cursor: 'nesw-resize', style: 'top: -3px; right: -3px;' },
    { position: 'sw', cursor: 'nesw-resize', style: 'bottom: -3px; left: -3px;' },
    { position: 'se', cursor: 'nwse-resize', style: 'bottom: -3px; right: -3px;' },
  ];

  // 获取图片宽高比
  const getAspectRatio = () => {
    const img = imgWrapper.querySelector('img');
    if (img && img.naturalWidth && img.naturalHeight) {
      return img.naturalWidth / img.naturalHeight;
    }
    return (asset.width || 1) / (asset.height || 1);
  };

  corners.forEach(({ position, cursor, style }) => {
    const handle = document.createElement('div');
    handle.className = `resize-handle-${position}`;
    handle.style.cssText = `
            position: absolute;
            width: 12px;
            height: 12px;
            border-radius: 7px;
            background: rgb(255, 255, 255);
            border: 2px solid rgb(23, 23, 23);
            box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 3px;
            pointer-events: auto;
            transition: 0.15s;
            ${style}
            cursor: ${cursor};
            z-index: 20;
            transform: scale(1);
        `;
    handle.onmouseenter = () => {
      handle.style.transform = 'scale(1.2)';
    };
    handle.onmouseleave = () => {
      handle.style.transform = 'scale(1)';
    };

    let isResizing = false;
    let startX = 0;
    let startY = 0;
    let startWidth = 0;
    let latestWidth = 0;
    let rafId = null;
    let pendingClientX = 0;
    let pendingClientY = 0;
    let prevTransition = '';
    let prevWillChange = '';

    handle.onmousedown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = imgWrapper.offsetWidth;
      latestWidth = startWidth;
      pendingClientX = startX;
      pendingClientY = startY;
      prevTransition = imgWrapper.style.transition;
      prevWillChange = imgWrapper.style.willChange;
      imgWrapper.style.transition = 'none';
      imgWrapper.style.willChange = 'width';
      container.setAttribute('draggable', 'false');

      // 添加全局 resize 样式
      document.body.style.cursor = cursor;
      document.body.style.userSelect = 'none';

      const aspectRatio = getAspectRatio();

      const applyResize = () => {
        rafId = null;
        if (!isResizing) return;

        let deltaX = pendingClientX - startX;
        let deltaY = pendingClientY - startY;

        if (position === 'nw' || position === 'sw') deltaX = -deltaX;
        if (position === 'nw' || position === 'ne') deltaY = -deltaY;

        const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY * aspectRatio;

        const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
        const effectiveMinWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);

        const clampedWidth = Math.max(
          effectiveMinWidth,
          Math.min(MAX_RESIZE_SIZE, startWidth + delta)
        );
        const newWidth = Math.round(clampedWidth);
        if (newWidth === latestWidth) return;

        latestWidth = newWidth;
        imgWrapper.style.width = `${newWidth}px`;
      };

      const onMouseMove = (e) => {
        if (!isResizing) return;
        pendingClientX = e.clientX;
        pendingClientY = e.clientY;
        if (rafId) return;
        rafId = requestAnimationFrame(applyResize);
      };

      const onMouseUp = () => {
        isResizing = false;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        imgWrapper.style.transition = prevTransition;
        imgWrapper.style.willChange = prevWillChange;
        asset.previewWidth = latestWidth;

        // 更新容器属性，实现 Textarea 数据闭环
        const aspectRatio = getAspectRatio();
        const newHeight = Math.round(latestWidth / aspectRatio);
        container.setAttribute('data-width', latestWidth);
        container.setAttribute('data-height', newHeight);

        container.setAttribute('draggable', 'true');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        onSync?.({ forceHeight: true });
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    handlesContainer.appendChild(handle);
  });

  return handlesContainer;
};

/**
 * 创建素材节点
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onRemove - 删除回调
 * @param {Function} options.onTagClick - 标签点击回调
 * @param {Function} options.onSync - 同步内容回调
 * @param {Function} options.makeDraggable - 使元素可拖拽的函数
 * @returns {HTMLElement} 素材容器元素
 */
export const createMaterialElement = (asset, options = {}) => {
  const { onRemove, onTagClick, onSync, makeDraggable } = options;

  const container = document.createElement('span');
  container.contentEditable = 'false';
  container.setAttribute('data-type', 'image');
  container.setAttribute('data-asset-id', asset.id);
  container.setAttribute('data-url', asset.url);
  container.setAttribute('data-tag', asset.tag || '');
  container.setAttribute('data-is-material', asset.isMaterial !== false ? 'true' : 'false');

  // 存储尺寸和名称信息，实现 Textarea 闭环，不依赖外部 assets 状态
  const w = asset.width || asset.meta?.w || 128;
  const h = asset.height || asset.meta?.h || 128;
  container.setAttribute('data-width', w);
  container.setAttribute('data-height', h);
  container.setAttribute('data-filename', asset.name || asset.filename || '');

  container.className =
    'material-node inline-block relative my-1.5 rounded-xl overflow-visible select-none group cursor-grab draggable-item transition-all duration-200';

  if (makeDraggable) {
    makeDraggable(container);
  }

  // 图片包装器
  const imgWrapper = document.createElement('div');
  imgWrapper.className =
    'img-wrapper relative rounded-xl overflow-hidden border-2 border-transparent transition-all duration-200 ring-1 ring-black/[0.08] hover:ring-black/[0.15]';
  // 计算宽高比，确保最小尺寸时保持比例
  const aspectRatio = (asset.width || 1) / (asset.height || 1);
  // 高度 >= MIN_PREVIEW_HEIGHT 是第一优先级，计算满足此条件的最小宽度
  const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
  // 取两者较大值，确保高度 >= MIN_PREVIEW_WIDTH 且满足最小高度比例
  const minWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);
  // 初始预览尺寸，始终应用最小限制（优先保证高度）
  const baseWidth = asset.previewWidth || asset.width || DEFAULT_PREVIEW_SIZE;
  const previewWidth = Math.min(Math.max(minWidth, baseWidth), MAX_PREVIEW_SIZE);
  imgWrapper.style.width = `${previewWidth}px`;
  imgWrapper.style.aspectRatio = `${asset.width || 1} / ${asset.height || 1}`;

  // 图片
  const img = document.createElement('img');
  img.src = asset.url;
  img.className = 'block w-full h-full object-contain pointer-events-none';
  img.draggable = false;
  img.onload = () => onSync?.({ forceHeight: true });
  img.onerror = () => onSync?.({ forceHeight: true });

  // 加载指示器覆盖层
  if (asset.loading) {
    const loaderOverlay = document.createElement('div');
    loaderOverlay.className =
      'loading-overlay absolute inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center rounded-xl';
    const loader = document.createElement('span');
    loader.className =
      'loading-spinner w-6 h-6 border border-white/30 border-t-white rounded-full animate-spin';
    loaderOverlay.appendChild(loader);
    imgWrapper.appendChild(loaderOverlay);
  }

  // 删除按钮 - 右上角，更简洁的设计
  const removeBtn = document.createElement('button');
  removeBtn.className =
    'remove-btn absolute top-2 right-2 w-5 h-5 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm backdrop-blur-sm z-10';
  removeBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const assetId = container.getAttribute('data-asset-id');
    container.remove();
    onRemove?.(assetId, asset.url);
    onSync?.();
  };

  // 类型指示器 - 左上角
  const typeIndicator = document.createElement('div');
  const isMaterial = asset.isMaterial !== false;
  typeIndicator.className =
    'type-indicator absolute top-2 left-2 w-5 h-5 rounded-full bg-white/90 text-neutral-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm backdrop-blur-sm z-10 pointer-events-none ring-1 ring-black/5';

  typeIndicator.innerHTML = isMaterial ? MATERIAL_ICON : REFERENCE_ICON;

  // 创建 4 角 resize handles
  const resizeHandles = createResizeHandles(imgWrapper, container, asset, onSync);

  // 选中状态管理
  let isSelected = false;

  const setSelected = (selected) => {
    isSelected = selected;
    if (selected) {
      resizeHandles.style.opacity = '1';
      imgWrapper.classList.add('border-neutral-900');
      imgWrapper.classList.remove('border-transparent');
    } else {
      resizeHandles.style.opacity = '0';
      imgWrapper.classList.remove('border-neutral-900');
      imgWrapper.classList.add('border-transparent');
    }
  };

  container._setSelected = setSelected;

  // 点击选中
  imgWrapper.onclick = (e) => {
    e.stopPropagation();
    deselectAllMaterialNodes(container);
    setSelected(!isSelected);
  };

  // 点击外部取消选中
  const handleClickOutside = (e) => {
    if (!container.contains(e.target)) {
      setSelected(false);
    }
  };
  document.addEventListener('click', handleClickOutside);

  // 存储清理函数
  container._cleanupResize = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  // 用途标签
  const usageLabel = document.createElement('div');
  usageLabel.className = `usage-trigger absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full transition-opacity duration-200 cursor-pointer z-10 max-w-[90%] ${
    asset.tag
      ? 'bg-black/60 text-white shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
      : 'bg-black/60 text-white/90 shadow-sm backdrop-blur-sm opacity-0 group-hover:opacity-100'
  }`;

  const labelText = document.createElement('span');
  labelText.className = 'truncate';
  labelText.textContent = asset.tag || 'Add usage';

  usageLabel.appendChild(labelText);

  // 点击触发器，通过回调打开外部 Vue Popover
  // 注意：必须从 DOM 节点获取最新的属性值，而不是从闭包中的 asset 对象获取
  // 因为上传完成后 asset-id 会被更新，但闭包中的 asset 对象不会被更新
  usageLabel.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onTagClick) {
      // 从 DOM 节点获取最新的属性值
      const currentId = container.getAttribute('data-asset-id');
      const currentUrl = container.getAttribute('data-url');
      const currentTag = container.getAttribute('data-tag') || '';
      const currentIsMaterial = container.getAttribute('data-is-material') !== 'false';
      onTagClick(
        {
          id: currentId,
          url: currentUrl,
          tag: currentTag,
          isMaterial: currentIsMaterial,
        },
        container,
        e
      );
    }
  };

  // 清理函数 - 存储在元素上以便后续清理
  container._cleanup = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  imgWrapper.appendChild(img);
  imgWrapper.appendChild(removeBtn);
  imgWrapper.appendChild(typeIndicator);
  imgWrapper.appendChild(usageLabel);
  container.appendChild(imgWrapper);
  container.appendChild(resizeHandles); // resize handles 放在外层容器，避免被 imgWrapper overflow 裁切

  return container;
};

/**
 * 创建简单图片节点（无用途标签，用于快速插入）
 * @param {Object} asset - 素材对象
 * @param {Object} options - 配置选项
 * @param {Function} options.onRemove - 删除回调
 * @param {Function} options.onSync - 同步内容回调
 * @param {Function} options.makeDraggable - 使元素可拖拽的函数
 * @returns {HTMLElement} 素材容器元素
 */
export const createSimpleMaterialElement = (asset, options = {}) => {
  const { onRemove, onSync, makeDraggable } = options;

  const container = document.createElement('span');
  container.contentEditable = 'false';
  container.setAttribute('data-type', 'image');
  container.setAttribute('data-asset-id', asset.id);
  container.setAttribute('data-url', asset.url);
  container.setAttribute('data-is-material', asset.isMaterial !== false ? 'true' : 'false');

  // 存储尺寸和名称信息，实现 Textarea 闭环
  const w = asset.width || asset.meta?.w || 128;
  const h = asset.height || asset.meta?.h || 128;
  container.setAttribute('data-width', w);
  container.setAttribute('data-height', h);
  container.setAttribute('data-filename', asset.name || asset.filename || '');

  container.className =
    'material-node inline-block relative my-1.5 rounded-xl overflow-visible select-none group cursor-grab draggable-item transition-all duration-200';

  if (makeDraggable) {
    makeDraggable(container);
  }

  // 图片包装器
  const imgWrapper = document.createElement('div');
  imgWrapper.className =
    'img-wrapper relative rounded-xl overflow-hidden border-2 border-transparent transition-all duration-200 ring-1 ring-black/[0.08] hover:ring-black/[0.15]';
  // 计算宽高比，确保最小尺寸时保持比例
  const aspectRatio = (asset.width || 1) / (asset.height || 1);
  // 高度 >= MIN_PREVIEW_HEIGHT 是第一优先级，计算满足此条件的最小宽度
  const minWidthForHeight = MIN_PREVIEW_HEIGHT * aspectRatio;
  // 取两者较大值，确保高度 >= MIN_PREVIEW_WIDTH
  const minWidth = Math.max(MIN_PREVIEW_WIDTH, minWidthForHeight);
  // 初始预览尺寸，始终应用最小限制（优先保证高度）
  const baseWidth = asset.previewWidth || asset.width || DEFAULT_PREVIEW_SIZE;
  const previewWidth = Math.min(Math.max(minWidth, baseWidth), MAX_PREVIEW_SIZE);
  imgWrapper.style.width = `${previewWidth}px`;
  imgWrapper.style.aspectRatio = `${asset.width || 1} / ${asset.height || 1}`;

  // 图片
  const img = document.createElement('img');
  img.src = asset.url;
  img.className = 'block w-full h-full object-contain pointer-events-none';
  img.draggable = false;
  img.onload = () => onSync?.({ forceHeight: true });
  img.onerror = () => onSync?.({ forceHeight: true });

  // 加载指示器覆盖层
  if (asset.loading) {
    const loaderOverlay = document.createElement('div');
    loaderOverlay.className =
      'loading-overlay absolute inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center rounded-xl';
    const loader = document.createElement('span');
    loader.className =
      'loading-spinner w-6 h-6 border border-white/30 border-t-white rounded-full animate-spin';
    loaderOverlay.appendChild(loader);
    imgWrapper.appendChild(loaderOverlay);
  }

  // 删除按钮 - 右上角，更简洁的设计
  const removeBtn = document.createElement('button');
  removeBtn.className =
    'remove-btn absolute top-2 right-2 w-5 h-5 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm backdrop-blur-sm z-10';
  removeBtn.innerHTML = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const assetId = container.getAttribute('data-asset-id');
    container.remove();
    onRemove?.(assetId, asset.url);
    onSync?.();
  };

  // 类型指示器 - 左上角
  const typeIndicator = document.createElement('div');
  const isMaterial = asset.isMaterial !== false;
  typeIndicator.className =
    'type-indicator absolute top-2 left-2 w-5 h-5 rounded-2 bg-white/90 text-neutral-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm backdrop-blur-sm z-10 pointer-events-none ring-1 ring-black/5';

  typeIndicator.innerHTML = isMaterial ? MATERIAL_ICON : REFERENCE_ICON;

  // 创建 4 角 resize handles
  const resizeHandles = createResizeHandles(imgWrapper, container, asset, onSync);

  // 选中状态管理
  let isSelected = false;

  const setSelected = (selected) => {
    isSelected = selected;
    if (selected) {
      resizeHandles.style.opacity = '1';
      imgWrapper.classList.add('border-neutral-900');
      imgWrapper.classList.remove('border-transparent');
    } else {
      resizeHandles.style.opacity = '0';
      imgWrapper.classList.remove('border-neutral-900');
      imgWrapper.classList.add('border-transparent');
    }
  };

  container._setSelected = setSelected;

  // 点击选中
  imgWrapper.onclick = (e) => {
    e.stopPropagation();
    deselectAllMaterialNodes(container);
    setSelected(!isSelected);
  };

  // 点击外部取消选中
  const handleClickOutside = (e) => {
    if (!container.contains(e.target)) {
      setSelected(false);
    }
  };
  document.addEventListener('click', handleClickOutside);

  // 存储清理函数
  container._cleanupResize = () => {
    document.removeEventListener('click', handleClickOutside);
  };

  imgWrapper.appendChild(img);
  imgWrapper.appendChild(removeBtn);
  imgWrapper.appendChild(typeIndicator);
  imgWrapper.appendChild(resizeHandles);
  container.appendChild(imgWrapper);

  return container;
};

/**
 * 更新图片节点（用于替换临时节点为真实素材）
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} tempId - 临时 ID
 * @param {Object} asset - 新的素材对象
 * @param {Function} onSync - 同步内容回调
 * @param {Object} extraOptions - 额外选项 { makeDraggable }
 */
export const updateMaterialNode = (editorRef, tempId, asset, onSync, extraOptions = {}) => {
  if (!editorRef) return;

  const node = editorRef.querySelector(`[data-asset-id="${tempId}"]`);
  if (!node) return;

  // 保存旧节点的位置
  const parent = node.parentNode;

  // 创建新节点 (使用 createMaterialElement 而不是 createSimpleMaterialElement，以支持用途标签)
  // 如果之前是简单节点，这里会升级为完整节点
  const newNode = createMaterialElement(asset, {
    onRemove: (assetId) => {
      // 需要重新绑定删除逻辑，因为 context 可能丢失
      const event = new CustomEvent('remove-material', { detail: { id: assetId } });
      editorRef.dispatchEvent(event);
    },
    onTagClick: (data, el, e) => {
      // 重新绑定点击逻辑
      const event = new CustomEvent('material-click', {
        detail: { data, element: el, event: e },
      });
      editorRef.dispatchEvent(event);
    },
    onSync,
    makeDraggable:
      extraOptions.makeDraggable ||
      ((el) => {
        el.setAttribute('draggable', 'true');
        el.classList.add('draggable-item');
      }),
  });

  // 替换节点
  parent.replaceChild(newNode, node);

  onSync?.();
};

/**
 * 更新素材标签
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} assetId - 素材 ID
 * @param {string} tag - 新标签
 * @param {boolean} isMaterial - 是否是素材
 * @param {Function} onSync - 同步内容回调
 * @param {HTMLElement} targetNode - 可选：指定要更新的节点（支持同 ID 多 tag）
 */
export const updateMaterialTagById = (editorRef, assetId, tag, isMaterial, onSync, targetNode) => {
  if (!editorRef) return;
  const node = targetNode || editorRef.querySelector(`[data-asset-id="${assetId}"]`);
  if (node) {
    node.setAttribute('data-tag', tag);
    if (typeof isMaterial === 'boolean') {
      node.setAttribute('data-is-material', isMaterial ? 'true' : 'false');
      updateTypeIndicator(node, isMaterial);
    }
    updateUsageLabel(node, tag);
    onSync?.();
  }
};

/**
 * 通过 ID 移除素材节点
 * @param {HTMLElement} editorRef - 编辑器元素
 * @param {string} assetId - 素材 ID
 * @param {Function} onSync - 同步内容回调
 */
export const removeMaterialNodeById = (editorRef, assetId, onSync) => {
  if (!editorRef) return;
  const node = editorRef.querySelector(`[data-asset-id="${assetId}"]`);
  if (node) {
    node.remove();
    onSync?.();
  }
};

/**
 * Get caret coordinates relative to viewport
 * @returns {Object|null} { x, y }
 */
export const getCaretCoordinates = () => {
  const selection = window.getSelection();
  if (selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  let rect = range.getBoundingClientRect();

  // If rect is empty (e.g. at start of empty line), try getClientRects
  if (rect.width === 0 && rect.height === 0) {
    const rects = range.getClientRects();
    if (rects.length > 0) {
      rect = rects[0];
    } else {
      // Fallback: use text node parent if possible or default to active element
      if (range.startContainer.nodeType === Node.ELEMENT_NODE) {
        const element = range.startContainer.childNodes[range.startOffset];
        if (element && element.getBoundingClientRect) {
          rect = element.getBoundingClientRect();
        }
      }
    }
  }

  // Return coordinates relative to viewport
  return {
    x: rect.left,
    y: rect.bottom,
  };
};
